/**
 * Communication client-state store — the single home for this feature's
 * out-of-Apollo-cache state: panel visibility / tab / drafts-view / expand pref,
 * the saved-draft count and send-in-flight flags, the page-published context,
 * and the compose-audience store (live + debounced halves). The service file
 * owns Apollo data + the permission gate; state lives here. See CLAUDE.md →
 * "Client state routes through the store factory".
 */
import _ from 'lodash';
import { useEffect, useMemo } from 'react';
import { makeVar, useReactiveVar } from '@apollo/client';

import { createPersistedStore, createStore } from './create-store';
import {
  DeliveryOption,
  IComposeAudienceSelection,
  IComposeContextRef
} from '../types/communication.types';

// Visibility of the global Communication side panel. dashboard-nav's launcher
// and the panel both read the hook below.
const [useIsCommunicationPanelOpen, , setCommunicationPanelOpen] = createStore(false);
export { useIsCommunicationPanelOpen };

// Device-scoped "expand the panel to a wider layout" preference. Persisted so it
// survives reloads/navigation (per-device, not per-account).
const [useIsCommunicationPanelExpanded, getCommunicationPanelExpanded, setCommunicationPanelExpanded] =
  createPersistedStore('mea:commPanelExpanded', false);
export { useIsCommunicationPanelExpanded };
export const toggleCommunicationPanelExpanded = () =>
  setCommunicationPanelExpanded(!getCommunicationPanelExpanded());

// Which body tab is active. Lifted out of the body's local state into a store so
// the header's drafts toggle can force the Compose tab when opening the list.
export type CommunicationPanelTab = 'compose' | 'history';
const [useCommunicationPanelTab, , setCommunicationPanelTab] =
  createStore<CommunicationPanelTab>('compose');
export { useCommunicationPanelTab, setCommunicationPanelTab };

// Whether the Compose tab shows the saved-drafts list instead of the compose
// form. Toggled from the header; read by the compose container.
const [useIsCommunicationDraftsView, getCommunicationDraftsView, setCommunicationDraftsView] =
  createStore(false);
export { useIsCommunicationDraftsView };

export const showCommunicationCompose = () => {
  setCommunicationDraftsView(false);
  setCommunicationPanelTab('compose');
};

export const toggleCommunicationDraftsView = () => {
  const next = !getCommunicationDraftsView();
  setCommunicationDraftsView(next);
  if (next) setCommunicationPanelTab('compose');
};

// Saved-draft count, published by the compose container so the header's drafts
// toggle can show a badge without reading localStorage itself.
const [useCommunicationDraftCount, , setCommunicationDraftCount] = createStore<number>(0);
export { useCommunicationDraftCount, setCommunicationDraftCount };

// Lightweight "compose from here" refs the current page publishes; the panel
// resolves each to a labeled chip. Publishers: player→PLAYER_ASSESSMENT, session→ASSESSMENT_SESSION.
const [usePageCommunicationContext, , setPageCommunicationContext] = createStore<IComposeContextRef[]>([]);
export { usePageCommunicationContext };

// Publish on mount / when the memoized refs change, clear on unmount so a stale
// page context never leaks into the panel.
export const usePublishCommunicationContext = (refs: IComposeContextRef[]) => {
  useEffect(() => {
    setPageCommunicationContext(refs);
    return () => setPageCommunicationContext([]);
  }, [refs]);
};

// Reactive flag set while a send mutation is in flight; the panel body reads it
// to lock tab switching (a Compose→History switch would unmount + drop state).
const [useIsCommunicationSendInFlight, , setCommunicationSendInFlight] = createStore(false);
export { useIsCommunicationSendInFlight, setCommunicationSendInFlight };

// Reset the send-in-flight lock on open so a leaked `true` doesn't keep the
// body's tab switching locked all session (keepMounted outlives the container).
export const openCommunicationPanel = () => {
  setCommunicationSendInFlight(false);
  setCommunicationDraftsView(false);
  setCommunicationPanelOpen(true);
};
export const closeCommunicationPanel = () => setCommunicationPanelOpen(false);

// Compose audience store — the ONE home for the form's query-facing selection
// (audience + content): the form owner publishes; count/preview read debounced.
const COMPOSE_AUDIENCE_DEBOUNCE_MS = 500;

const EMPTY_COMPOSE_AUDIENCE: IComposeAudienceSelection = {
  contexts: [],
  audience: {},
  deliveryOption: DeliveryOption.EMAIL_ONLY,
  content: { subject: '', bodyHtml: '', smsBody: '' }
};

export const composeAudienceVar = makeVar<IComposeAudienceSelection>(EMPTY_COMPOSE_AUDIENCE);
export const composeAudienceDebouncedVar = makeVar<IComposeAudienceSelection>(EMPTY_COMPOSE_AUDIENCE);

// Module singleton on purpose: one trailing-edge push serializes every
// publisher, so consumers can never observe two competing debounce windows.
const pushComposeAudienceDebounced = _.debounce(
  (selection: IComposeAudienceSelection) => composeAudienceDebouncedVar(selection),
  COMPOSE_AUDIENCE_DEBOUNCE_MS
);

export const publishComposeAudience = (selection: IComposeAudienceSelection) => {
  composeAudienceVar(selection);
  pushComposeAudienceDebounced(selection);
};

// Draft open/switch + mount seeding: flush both halves now and cancel any
// pending push so a prior draft's selection can't land after the switch.
export const resetComposeAudience = (selection: IComposeAudienceSelection) => {
  pushComposeAudienceDebounced.cancel();
  composeAudienceVar(selection);
  composeAudienceDebouncedVar(selection);
};

// Submit path: land any pending push now so a fresh preview/validation query
// fires immediately instead of after the trailing debounce edge.
export const flushComposeAudience = () => pushComposeAudienceDebounced.flush();

export const useDebouncedComposeAudience = (): {
  selection: IComposeAudienceSelection;
  isStale: boolean;
  isPreviewStale: boolean;
} => {
  const live = useReactiveVar(composeAudienceVar);
  const selection = useReactiveVar(composeAudienceDebouncedVar);
  // Deep compares — publishes carry fresh object refs. isStale ignores content
  // (the count doesn't read it); isPreviewStale covers everything preview reads.
  const isStale = useMemo(
    () => !_.isEqual(_.omit(live, 'content'), _.omit(selection, 'content')),
    [live, selection]
  );
  const isPreviewStale = useMemo(() => !_.isEqual(live, selection), [live, selection]);
  return { selection, isStale, isPreviewStale };
};
